import { useLanguage } from "../utils/LanguageContext";
import React, { useState, useEffect, useRef } from "react";
import { Search, Phone, Menu, X, ChevronRight } from 'lucide-react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import { useAboutData } from "../hooks/useAboutData";
import axios from 'axios';
import SearchBox from "./SearchBox";

const API_BASE_URL = 'https://texla.uz/api/v1';

const getLocalizedText = (obj, language) => {
    if (!obj) return '';
    const key = `title_${language}`;
    return obj[key] || obj.title_uz || obj.title || '';
};


const Header = () => {
    const { about } = useAboutData();
    const phone = about?.phone || '+998...';
    const { language, setLanguage, t } = useLanguage();
    const [showCatalog, setShowCatalog] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showMobileCatalog, setShowMobileCatalog] = useState(false);
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [catalogSearch, setCatalogSearch] = useState('');
    const catalogRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => { fetchCategories(); }, []);

    useEffect(() => {
        if (showCatalog || mobileMenuOpen || showMobileCatalog) {
            if (categories.length === 0 && !loadingCategories) fetchCategories();
        }
    }, [showCatalog, mobileMenuOpen, showMobileCatalog]);

    // Tashqariga bosganda yopish
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (catalogRef.current && !catalogRef.current.contains(e.target)) {
                setShowCatalog(false);
            }
        };
        if (showCatalog) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCatalog]);

    // Scroll lock body
    useEffect(() => {
        if (showCatalog || mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [showCatalog, mobileMenuOpen]);

    const fetchCategories = async () => {
        setLoadingCategories(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/categories/`);
            setCategories(response.data || []);
        } catch {
            setCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    };

    const location = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [location]);

    const handleCategoryClick = (categoryId) => {
        window.scrollTo(0, 0);
        setShowCatalog(false);
        setCatalogSearch('');
        setMobileMenuOpen(false);
        navigate(`/shop?category=${categoryId}`);
    };

    return (
        <>
            {/* ═══════════════════════════════════════════════════
                MEGA KATALOG — To'liq ekran dropdown
            ═══════════════════════════════════════════════════ */}
            {showCatalog && (
                <div className="fixed inset-0 z-50">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setShowCatalog(false)}
                    />

                    {/* Katalog paneli — 100vh, ichida scroll */}
                    <div
                        ref={catalogRef}
                        className="absolute left-0 right-0 top-0 bg-white shadow-xl flex flex-col"
                        style={{ height: '100vh' }}
                    >
                        {/* Panel header — och kulrang */}
                        <div className="bg-gray-100 border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
                            <h2 className="text-gray-700 font-semibold text-base">
                                {t('header.catalog')}
                            </h2>
                            <button
                                onClick={() => { setShowCatalog(false); setCatalogSearch(''); }}
                                className="text-gray-400 hover:text-gray-700 hover:bg-gray-200 p-1.5 rounded-lg transition"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Kategoriyalar — scroll qism */}
                        <div className="flex-1 overflow-y-auto px-6 py-5">
                            <div className="max-w-7xl mx-auto">
                                {/* Qidiruv — katta, markazda */}
                                <div className="relative mb-5 max-w-lg mx-auto">
                                    <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        type="text"
                                        value={catalogSearch}
                                        onChange={e => setCatalogSearch(e.target.value)}
                                        placeholder={t('header.searchCatalog')}
                                        className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white shadow-sm"
                                    />
                                    {catalogSearch && (
                                        <button
                                            onClick={() => setCatalogSearch('')}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X size={15} />
                                        </button>
                                    )}
                                </div>
                                {loadingCategories ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                        {[...Array(12)].map((_, i) => (
                                            <div key={i} className="h-11 bg-gray-100 rounded-lg animate-pulse" />
                                        ))}
                                    </div>
                                ) : categories.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                        {categories
                                            .filter(cat => getLocalizedText(cat, language).toLowerCase().includes(catalogSearch.toLowerCase()))
                                            .map(cat => (
                                                <button
                                                    key={cat.id}
                                                    onClick={() => handleCategoryClick(cat.id)}
                                                    className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300 transition text-left w-full group"
                                                >
                                                <span className="text-sm text-gray-700 group-hover:text-gray-900 leading-tight">
                                                    {getLocalizedText(cat, language)}
                                                </span>
                                                    <ChevronRight size={14} className="text-gray-300 group-hover:text-gray-500 flex-shrink-0 ml-2 transition" />
                                                </button>
                                            ))}
                                        {catalogSearch && categories.filter(cat => getLocalizedText(cat, language).toLowerCase().includes(catalogSearch.toLowerCase())).length === 0 && (
                                            <div className="col-span-4 text-center py-8 text-gray-400 text-sm">
                                                `${catalogSearch} ${t('header.noResults')}`
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-400">
                                        <p className="text-sm">{t('header.noCatalog')}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-200 bg-gray-50 px-6 py-3 flex-shrink-0">
                            <button
                                onClick={() => { setShowCatalog(false); navigate('/shop'); }}
                                className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 text-sm font-medium transition"
                            >
                                <span>{t('header.viewAllProducts')}</span>
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════
                MOBILE MENU
            ═══════════════════════════════════════════════════ */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 flex lg:hidden">
                    <div className="w-72 bg-white shadow-2xl flex flex-col" style={{ maxHeight: '100vh' }}>
                        {/* Mobile header */}
                        <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-4 py-3 flex items-center justify-between flex-shrink-0">
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setShowMobileCatalog(false)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                                        !showMobileCatalog ? 'bg-white text-orange-600' : 'text-white/80 hover:text-white'
                                    }`}
                                >
                                    {t('header.menu')}
                                </button>
                                <button
                                    onClick={() => setShowMobileCatalog(true)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                                        showMobileCatalog ? 'bg-white text-orange-600' : 'text-white/80 hover:text-white'
                                    }`}
                                >
                                    {t('header.catalog')}
                                </button>
                            </div>
                            <button
                                onClick={() => setMobileMenuOpen(false)}
                                className="text-white/80 hover:text-white p-1"
                            >
                                <X size={22} />
                            </button>
                        </div>

                        {/* Mobile content — scroll */}
                        <div className="flex-1 overflow-y-auto">
                            {showMobileCatalog ? (
                                <div className="p-3">
                                    {/* Mobile catalog search */}
                                    <div className="relative mb-3">
                                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        <input
                                            type="text"
                                            value={catalogSearch}
                                            onChange={e => setCatalogSearch(e.target.value)}
                                            placeholder={t('header.searchCatalog')}
                                            className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 bg-gray-50"
                                        />
                                        {catalogSearch && (
                                            <button onClick={() => setCatalogSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                                <X size={13} />
                                            </button>
                                        )}
                                    </div>
                                    {loadingCategories ? (
                                        <div className="space-y-2 p-2">
                                            {[...Array(6)].map((_, i) => (
                                                <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="space-y-1">
                                            {categories
                                                .filter(cat => getLocalizedText(cat, language).toLowerCase().includes(catalogSearch.toLowerCase()))
                                                .map(cat => (
                                                    <button
                                                        key={cat.id}
                                                        onClick={() => {
                                                            handleCategoryClick(cat.id);
                                                            setMobileMenuOpen(false);
                                                        }}
                                                        className="w-full flex items-center justify-between px-3 py-3 hover:bg-gray-100 rounded-lg transition text-sm text-gray-700 font-medium text-left"
                                                    >
                                                        <span className="flex-1">{getLocalizedText(cat, language)}</span>
                                                        <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />
                                                    </button>
                                                ))}
                                            {catalogSearch && categories.filter(cat => getLocalizedText(cat, language).toLowerCase().includes(catalogSearch.toLowerCase())).length === 0 && (
                                                <p className="text-xs text-gray-400 text-center py-4">{t('shop.notFound')}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="p-4">
                                    <SearchBox isMobile={true} />
                                    <div className="space-y-1 text-sm mt-4">
                                        <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block p-3 hover:bg-gray-100 rounded-lg font-medium text-gray-800">{t('header.aboutCompany')}</Link>
                                        <Link to="/shop" onClick={() => setMobileMenuOpen(false)} className="block p-3 hover:bg-gray-100 rounded-lg font-medium text-gray-800">{t('header.catalog')}</Link>
                                        <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block p-3 hover:bg-gray-100 rounded-lg font-medium text-gray-800">{t('header.news')}</Link>
                                        <Link to="/faqs" onClick={() => setMobileMenuOpen(false)} className="block p-3 hover:bg-gray-100 rounded-lg font-medium text-gray-800">{t('header.faqs')}</Link>
                                        <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block p-3 hover:bg-gray-100 rounded-lg font-medium text-gray-800">{t('header.contact')}</Link>
                                    </div>
                                    <div className="border-t mt-4 pt-4">
                                        <p className="text-xs text-gray-500 mb-2 font-medium">{t('language.languageLabel')}</p>
                                        <div className="flex gap-2">
                                            <button onClick={() => setLanguage('uz')} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition ${language === 'uz' ? 'bg-green-100 text-green-800 font-bold' : 'bg-gray-100 hover:bg-gray-200'}`}>
                                                🇺🇿 {t('language.uz')}
                                            </button>
                                            <button onClick={() => setLanguage('ru')} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition ${language === 'ru' ? 'bg-red-100 text-red-800 font-bold' : 'bg-gray-100 hover:bg-gray-200'}`}>
                                                🇷🇺 {t('language.ru')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex-1 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
                </div>
            )}

            {/* ═══════════════════════════════════════════════════
                TOP NAV
            ═══════════════════════════════════════════════════ */}
            <nav className="hidden lg:block bg-white border-b">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex items-center justify-between h-12 text-sm">
                        <div className="flex gap-1">
                            <Link to="/about" className="px-3 py-2 hover:bg-gray-100 rounded-lg text-gray-700 hover:text-gray-900 transition">{t('header.aboutCompany')}</Link>
                            <Link to="/shop" className="px-3 py-2 hover:bg-gray-100 rounded-lg text-gray-700 hover:text-gray-900 transition">{t('header.catalog')}</Link>
                            <Link to="/blog" className="px-3 py-2 hover:bg-gray-100 rounded-lg text-gray-700 hover:text-gray-900 transition">{t('header.news')}</Link>
                            <Link to="/faqs" className="px-3 py-2 hover:bg-gray-100 rounded-lg text-gray-700 hover:text-gray-900 transition">{t('header.faqs')}</Link>
                            <Link to="/contact" className="px-3 py-2 hover:bg-gray-100 rounded-lg text-gray-700 hover:text-gray-900 transition">{t('header.contact')}</Link>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setLanguage('uz')} className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition ${language === 'uz' ? 'bg-green-100 text-green-900 font-bold' : 'bg-gray-100 hover:bg-gray-200'}`}>
                                🇺🇿 {t('language.uz')}
                            </button>
                            <button onClick={() => setLanguage('ru')} className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition ${language === 'ru' ? 'bg-red-100 text-red-900 font-bold' : 'bg-gray-100 hover:bg-gray-200'}`}>
                                🇷🇺 {t('language.ru')}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* ═══════════════════════════════════════════════════
                MAIN HEADER — sticky
            ═══════════════════════════════════════════════════ */}
            <header className="bg-white shadow-md sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 py-3 lg:py-4">
                    <div className="flex items-center justify-between gap-4">
                        <Link to={"/"} className="flex-shrink-0">
                            {about?.logo && (
                                <img
                                    src={about.logo}
                                    alt="Logo"
                                    className="h-[80px] sm:h-[100px] lg:h-[115px] w-auto object-contain"
                                />
                            )}
                        </Link>

                        {/* Katalog tugmasi */}
                        <button
                            onClick={() => setShowCatalog(!showCatalog)}
                            className={`hidden sm:flex items-center gap-2 px-4 lg:px-6 py-2.5 rounded-lg font-medium text-sm lg:text-base flex-shrink-0 transition ${
                                showCatalog
                                    ? 'bg-orange-600 text-white shadow-lg'
                                    : 'bg-white border border-gray-300 text-gray-900 hover:bg-orange-50 hover:border-orange-300'
                            }`}
                        >
                            <Menu size={18} className={showCatalog ? 'text-white' : ''} />
                            <span>{t('header.catalog')}</span>
                        </button>

                        <div className="hidden sm:flex flex-1 min-w-[200px] max-w-xl">
                            <SearchBox isMobile={false} />
                        </div>

                        <div className="text-right flex-shrink-0">
                            <div className="hidden lg:block text-xs lg:text-sm text-gray-600">{t('header.workingHours')}</div>
                            <a href={`tel:${phone}`} className="text-sm lg:text-lg font-bold text-gray-900 inline-flex items-center gap-1 whitespace-nowrap">
                                <Phone size={16} className="text-orange-600" />
                                {phone}
                            </a>
                        </div>

                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden text-gray-900 flex-shrink-0"
                        >
                            <Menu size={28} />
                        </button>
                    </div>
                </div>
            </header>
        </>
    );
};

export default Header;